import {
  Component,
  InjectionToken,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { LoggerService } from 'apps/purchasing-portal/src/app/logger.service';
import {
  getSelectedWarnings,
  TixCompanyPartialState
} from '@tix/company/state';
import * as CompanyActions from '@tix/company/state/actions';
import * as CompanySelectors from '@tix/company/state/selectors';
import {
  Maybe,
  TixAddress,
  TixBusinessCodeGroup,
  TixCompanyWarning,
  TixMediaFile,
  TixPhoneNumber,
  TixSocialMedia,
  TixWarning
} from '@tix/data-access';
import { CompanyEditSection, TixCompanyProfile } from '@tix/shared/models';
import {
  BUSINESS_CODE_GROUP_NAMES,
  TixBusinessGroupsService
} from '@tix/shared/state';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { deleteCompanyWarning } from '@tix/company/state/actions';
import {
  ComponentCanDeactivate,
  TixAddressEditComponent,
  TixMediaFilesEditComponent,
  TixPhoneNumbersComponent,
  TixSocialMediaComponent
} from '@tix/shared/ui/components';
import { TixCompanyProfileComponent } from 'libs/company/components/src/lib/profile/profile.component';
import { WarningComponent } from 'libs/shared/ui/components/src/lib/warning/warning.component';
import { MatAccordion } from '@angular/material/expansion';
import { UserSelector } from '@tix/auth/state';
import { map, take } from 'rxjs/operators';
import { TixAnalyticsService } from '@tix/shared/analytics';

/**
 * Injection token that can be used to inject an instances of `TixCompanyAdminPage`. It serves
 * as alternative token to the actual `TixCompanyAdminPage` class which would cause unnecessary
 * retention of the `TixCompanyAdminPage` class and its component metadata.
 */
export const TIX_COMPANY_ADMIN_PAGE = new InjectionToken<TixCompanyAdminPage>(
  'TixCompanyAdminPage'
);

@Component({
  selector: 'tix-company-admin',
  templateUrl: './company-admin.component.html',
  styleUrls: ['./company-admin.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'tix-company-admin'
  }
})
export class TixCompanyAdminPage implements OnInit, ComponentCanDeactivate {
  addNewContact(contact: any): void {
    this.analytics.captureEvent('staff_add_new_contact', { contact });
  }

  selectContactFromEmailList(email: string): void {
    this.analytics.captureEvent('staff_select_contact_from_email_list', {
      email
    });
  }

  updateStaffRoles(roles: any): void {
    this.analytics.captureEvent('staff_update_roles', { roles });
  }

  clickRolesInfoIcon(): void {
    this.analytics.captureEvent('staff_roles_info_icon_clicked');
  }

  addPhoneNumber(phoneNumber: any): void {
    this.analytics.captureEvent('staff_add_phone_number', { phoneNumber });
  }

  clickDeleteStaff(staffId: string): void {
    this.analytics.captureEvent('staff_delete_clicked', { staffId });
  }

  confirmDeleteStaff(staffId: string): void {
    this.analytics.captureEvent('staff_delete_confirmed', { staffId });
  }

  cancelDeleteStaff(staffId: string): void {
    this.analytics.captureEvent('staff_delete_cancelled', { staffId });
  }

  userId$ = this.store.select(UserSelector.getAuthenticatedUser).pipe(
    map(u => u?.uid),
    take(1)
  );

  readonly companyId$: Observable<string | undefined> = this.store.select(
    CompanySelectors.getSelectedCompanyId
  );
  readonly companyName$: Observable<string | undefined> = this.store.select(
    CompanySelectors.getSelectedName
  );
  readonly companyUrl$: Observable<Maybe<string>> = this.store.select(
    CompanySelectors.getSelectedUrl
  );
  readonly companyAddresses$: Observable<Maybe<TixAddress>[] | undefined> =
    this.store.select(CompanySelectors.getSelectedAddresses);
  readonly companyProfilLoading$: Observable<boolean | undefined> =
    this.store.select(CompanySelectors.getSelectedLoadingProfile);
  readonly companyLoadingAdress$: Observable<boolean | undefined> =
    this.store.select(CompanySelectors.getSelectedLoadingAdress);
  readonly companyLoadingMedia$: Observable<boolean | undefined> =
    this.store.select(CompanySelectors.getSelectedLoadingMedia);
  readonly companyLoadingMediaFiles$: Observable<boolean | undefined> =
    this.store.select(CompanySelectors.getSelectedLoadingMediaFiles);
  readonly companyLoadingPhone$: Observable<boolean | undefined> =
    this.store.select(CompanySelectors.getSelectedLoadingPhone);
  readonly companyPhoneNumbers$: Observable<
    Maybe<TixPhoneNumber>[] | undefined
  > = this.store.select(CompanySelectors.getSelectedPhoneNumbers);
  readonly companySocialMedias$: Observable<
    Maybe<TixSocialMedia>[] | undefined
  > = this.store.select(CompanySelectors.getSelectedSocialMedias);
  readonly companyWarnings$: Observable<Maybe<TixWarning>[] | undefined> =
    this.store.select(CompanySelectors.getSelectedWarnings);
  readonly companyMediaFiles$: Observable<Maybe<TixMediaFile>[] | undefined> =
    this.store.select(CompanySelectors.getSelectedMedias);

  // Main loading state that combines all individual loading states
  private initialLoadingSubject = new BehaviorSubject<boolean>(true);

  readonly loading$ = combineLatest([
    this.initialLoadingSubject.asObservable(),
    this.companyProfilLoading$,
    this.companyLoadingAdress$,
    this.companyLoadingMedia$,
    this.companyLoadingMediaFiles$,
    this.companyLoadingPhone$
  ]).pipe(
    map(
      ([
        initialLoading,
        profileLoading,
        addressLoading,
        mediaLoading,
        mediaFilesLoading,
        phoneLoading
      ]) =>
        initialLoading ||
        profileLoading ||
        addressLoading ||
        mediaLoading ||
        mediaFilesLoading ||
        phoneLoading
    )
  );

  /**
   * BusinessCodes
   */
  readonly businessPhoneTypes$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.BUSINESS_PHONE_TYPE
    );

  readonly businessCountryCodes$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.COUNTRY_CODE
    );
  readonly businessStateProvinces$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.STATE_PROVINCE
    );
  readonly businessSocialMediaTypes$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.SOCIAL_MEDIA_TYPE
    );
  readonly businessMediaFilesTypes$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.MEDIA_FILE_TYPE
    );
  readonly businessWarningTypes$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.WARNING_TYPE
    );

  isEditMode = false;
  isNewCompany = false;
  companyId = '';
  @ViewChild(TixCompanyProfileComponent)
  companyProfileComp?: TixCompanyProfileComponent;
  @ViewChild(TixPhoneNumbersComponent)
  phoneNumbersComp?: TixPhoneNumbersComponent;
  @ViewChild(TixAddressEditComponent) addressEditComp?: TixAddressEditComponent;
  @ViewChild(TixMediaFilesEditComponent)
  mediaFilesEditComp?: TixMediaFilesEditComponent;
  @ViewChild(TixSocialMediaComponent)
  socialMediaEditComp?: TixSocialMediaComponent;
  @ViewChild(WarningComponent) warningComp?: WarningComponent;

  @ViewChild(MatAccordion) accordion: MatAccordion;
  accordionStatus: 'open' | 'close' = 'open';
  toggleAccordion(state: 'open' | 'close'): void {
    this.analytics.captureEvent(
      state === 'open'
        ? 'company_expand_all_clicked'
        : 'company_collapse_all_clicked'
    );
    this.accordionStatus = state;
    if (this.accordionStatus === 'open') {
      this.accordion.openAll();
    } else {
      this.accordion.closeAll();
    }
  }

  canDeactivate() {
    if (
      this.companyProfileComp?.profileForm.dirty ||
      this.phoneNumbersComp?.phoneNumbersForm.dirty ||
      this.addressEditComp?.addressForm.dirty ||
      this.warningComp?.warningForm.dirty
    ) {
      return false;
    }

    return true;
  }

  constructor(
    private readonly store: Store<TixCompanyPartialState>,
    private readonly analytics: TixAnalyticsService,
    private readonly route: ActivatedRoute,
    private businessGroupsService: TixBusinessGroupsService,
    private logger: LoggerService
  ) {
    this.route.url.subscribe((url: any) => {
      this.isEditMode = url[0]?.path !== 'add';
      if (!this.isEditMode) {
        this.store.dispatch(CompanyActions.deselectCompany());
      } else this.isNewCompany = true;
    });
    this.route.queryParams.subscribe((queryParam: any) => {
      if (queryParam['isNew']) {
        this.isNewCompany = true;
      }
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.companyId = params['companyId'];

      // Set initial loading to false after a short delay to allow data to load
      setTimeout(() => {
        this.initialLoadingSubject.next(false);
      }, 1000);

      // Log company admin view
      this.userId$.subscribe(userId => {
        this.logger.info({
          message: 'Company admin page viewed',
          event: 'company_admin_view',
          data: {
            companyId: this.companyId,
            isNewCompany: this.isNewCompany,
            isEditMode: this.isEditMode
          }
        });
      });
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.toggleAccordion('open');
    }, 0);
  }

  //#region UI Callbacks

  /* #region Save */

  saveCompanyProfile(profile: TixCompanyProfile): void {
    this.analytics.captureEvent('staff_save_profile', {
      company_id: profile.companyId,
      name: profile.name,
      url: profile.url
    });
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: this.isEditMode
          ? 'Company profile updated'
          : 'New company created',
        event: this.isEditMode
          ? 'company_profile_update'
          : 'company_profile_create',
        data: {
          companyId: profile.companyId,
          name: profile.name,
          url: profile.url
        }
      });
      this.analytics.captureEvent('staff_save_profile', {
        company_id: profile.companyId,
        name: profile.name,
        url: profile.url
      });
    });

    if (this.isEditMode && profile.companyId) {
      this.store.dispatch(CompanyActions.saveCompanyProfile({ profile }));
    } else {
      this.store.dispatch(
        CompanyActions.insertCompanyProfile({
          name: profile.name,
          url: profile.url
        })
      );
    }
  }

  savePhoneNumbers(phoneNumbers: TixPhoneNumber[]): void {
    this.analytics.captureEvent('staff_save_phone_numbers', {
      company_id: this.companyId,
      phone_numbers: phoneNumbers.map(p => ({
        id: p.phoneNumberId,
        number: p.phoneNumber,
        type: p.type
      }))
    });
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company phone numbers updated',
        event: 'company_phone_numbers_update',
        data: {
          companyId: this.companyId,
          phoneNumbers: phoneNumbers.map(p => ({
            id: p.phoneNumberId,
            number: p.phoneNumber,
            type: p.type
          }))
        }
      });
      this.analytics.captureEvent('company_phone_numbers_update', {
        companyId: this.companyId,
        phoneNumbers: phoneNumbers.map(p => ({
          id: p.phoneNumberId,
          number: p.phoneNumber,
          type: p.type
        }))
      });
    });

    this.store.dispatch(
      CompanyActions.saveCompanyPhoneNumbers({ phoneNumbers })
    );
  }

  saveAddress(address: TixAddress): void {
    this.analytics.captureEvent('address_saved');
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company address updated',
        event: 'company_address_update',
        data: {
          companyId: this.companyId,
          addressId: address.addressId,
          countryCode: address.countryCode,
          state: address.state,
          city: address.city
        }
      });
      this.analytics.captureEvent('company_address_update', {
        companyId: this.companyId,
        addressId: address.addressId,
        countryCode: address.countryCode,
        state: address.state,
        city: address.city
      });
    });

    this.store.dispatch(CompanyActions.saveCompanyAddress({ address }));
  }

  saveSocialMedias(socialMedias: Partial<TixSocialMedia>[]): void {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company social media updated',
        event: 'company_social_media_update',
        data: {
          companyId: this.companyId,
          socialMedias: socialMedias.map(sm => ({
            id: sm.socialMediaId,
            type: sm.socialMediaType,
            url: sm.url
          }))
        }
      });

      this.analytics.captureEvent('company_social_media_update', {
        companyId: this.companyId,
        socialMedias: socialMedias.map(sm => ({
          id: sm.socialMediaId,
          type: sm.socialMediaType,
          url: sm.url
        }))
      });
    });

    this.store.dispatch(
      CompanyActions.saveCompanySocialMedias({ socialMedias })
    );
  }

  saveWarnings(warnings: Partial<TixWarning>[]): void {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company warnings updated',
        event: 'company_warnings_update',
        data: {
          companyId: this.companyId,
          warnings: warnings.map(w => ({
            id: w.warningId,
            type: w.type,
            message: w.message
          }))
        }
      });
      this.analytics.captureEvent('company_warnings_update', {
        companyId: this.companyId,
        warnings: warnings.map(w => ({
          id: w.warningId,
          type: w.type,
          message: w.message
        }))
      });
    });

    this.store.dispatch(CompanyActions.saveCompanyWarnings({ warnings }));
  }

  saveMediaFile(mediaFiles: Partial<TixMediaFile>[]) {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company media files updated',
        event: 'company_media_files_update',
        data: {
          companyId: this.companyId,
          mediaFiles: mediaFiles.map(mf => ({
            id: mf.mediaFileId,
            type: mf.fileType
          }))
        }
      });
      this.analytics.captureEvent('company_media_files_update', {
        companyId: this.companyId,
        mediaFiles: mediaFiles.map(mf => ({
          id: mf.mediaFileId,
          type: mf.fileType
        }))
      });
    });

    this.store.dispatch(CompanyActions.saveCompanyMediaFiles({ mediaFiles }));
  }

  /* #endregion */

  //#region Delete callbacks

  deletePhoneNumber(phoneNumber: TixPhoneNumber): void {
    this.analytics.captureEvent('staff_delete_phone_number', {
      company_id: this.companyId,
      phone_number_id: phoneNumber.phoneNumberId,
      number: phoneNumber.phoneNumber,
      type: phoneNumber.type
    });
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company phone number deleted',
        event: 'company_phone_number_delete',
        data: {
          companyId: this.companyId,
          phoneNumberId: phoneNumber.phoneNumberId,
          number: phoneNumber.phoneNumber,
          type: phoneNumber.type
        }
      });
      this.analytics.captureEvent('company_phone_number_delete', {
        companyId: this.companyId,
        phoneNumberId: phoneNumber.phoneNumberId,
        number: phoneNumber.phoneNumber,
        type: phoneNumber.type
      });
    });

    this.store.dispatch(
      CompanyActions.deleteCompanyPhoneNumber({ phoneNumber })
    );
  }

  deleteSocialMedia(socialMedia: TixSocialMedia): void {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company social media deleted',
        event: 'company_social_media_delete',
        data: {
          companyId: this.companyId,
          socialMediaId: socialMedia.socialMediaId,
          type: socialMedia.socialMediaType,
          url: socialMedia.url
        }
      });
      this.analytics.captureEvent('company_social_media_delete', {
        companyId: this.companyId,
        socialMediaId: socialMedia.socialMediaId,
        type: socialMedia.socialMediaType,
        url: socialMedia.url
      });
    });

    this.store.dispatch(
      CompanyActions.deleteCompanySocialMedia({ socialMedia })
    );
  }

  deleteMediaFile(mediaFile: TixMediaFile): void {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company media file deleted',
        event: 'company_media_file_delete',
        data: {
          companyId: this.companyId,
          mediaFileId: mediaFile.mediaFileId,
          type: mediaFile.fileType
        }
      });
      this.analytics.captureEvent('company_media_file_delete', {
        companyId: this.companyId,
        mediaFileId: mediaFile.mediaFileId,
        type: mediaFile.fileType
      });
    });

    this.store.dispatch(CompanyActions.deleteCompanyMediaFile({ mediaFile }));
  }

  deleteWarning(warning: TixWarning): void {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company warning deleted',
        event: 'company_warning_delete',
        data: {
          companyId: this.companyId,
          warningId: warning.warningId,
          type: warning.type,
          message: warning.message
        }
      });
      this.analytics.captureEvent('company_warning_delete', {
        companyId: this.companyId,
        warningId: warning.warningId,
        type: warning.type,
        message: warning.message
      });
    });

    this.store.dispatch(CompanyActions.deleteCompanyWarning({ warning }));
  }

  //#endregion

  editSection(section: CompanyEditSection): void {
    this.userId$.subscribe(userId => {
      this.logger.info({
        message: 'Company section edit started',
        event: 'company_section_edit',
        data: {
          companyId: this.companyId,
          section
        }
      });
      this.analytics.captureEvent('company_section_edit', {
        companyId: this.companyId,
        section
      });
    });

    this.store.dispatch(CompanyActions.editCompanySection({ section }));
  }

  //#endregion
}
